import { narrationTimingGuidance } from 'js/src/agent/skills/assetTimingGuidance';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const aiAvatarVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.aiAvatarVideoBasics,
    description: `Build a video around an AI avatar — a generated on-screen presenter that speaks a script you write, in an AI voice. Load BEFORE adding an avatar scene group. For a real recording of a person, use ${SKILL_NAMES.talkingHeadVideoBasics}; for narration with no on-screen speaker, use ${SKILL_NAMES.aiVoiceoverVideoBasics}.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# AI Avatar Video Basics

An AI avatar is a **generated** presenter who appears on screen and speaks your script in an
AI voice. The presenter and the voice are both synthesized — there is no recording and nothing
to upload. The avatar is the point: it stays visible, and overlays are accents on top of it.

## Creating the avatar scene group
The avatar is generated from a script. If the user hasn't given you one, write it yourself from
their request and any source material — do not ask the user to write it, and never ask them to
record anything.
- \`${TOOL_NAMES.addAvatarSceneGroup}\` generates the avatar, its AI voice, and the synced
  transcript in one step from the **plain** script. Create a single avatar scene group for the
  narration (never one per scene), then build overlays over it. Place it with
  \`after_scene_group_id\`, or omit that to open the video.
- Picking the presenter: ask an \`avatar\` question with \`${TOOL_NAMES.askUserQuestion}\` — it shows
  the visual avatar gallery where the user previews presenters (and their voices) and picks one,
  and resolves to the chosen avatar's id, which you pass as \`avatar_id\`. Do this once, up front,
  when building an avatar video. Skip it and pass \`avatar_id\` directly (or omit it for the
  default avatar) only when the user already named a presenter or clearly doesn't care.
  \`${TOOL_NAMES.listAvatars}\` still lists the avatars as text when you need to reason over them.
- Voice: picking the avatar already sets the voice — each avatar comes with a paired voice, and
  the picker previews it. So do NOT also ask a separate voice question; for an avatar video the
  avatar question is the one and only presenter/voice choice. Change the voice only if the user
  explicitly asks for a different one — then find it with \`${TOOL_NAMES.searchVoices}\` and pass its
  slug as \`voice_slug\`.

## Adding pauses
Pauses give narration room to breathe. The two-step procedure:
1. Generate the avatar from the **plain** script (\`${TOOL_NAMES.addAvatarSceneGroup}\` — pause
   tags in the initial transcript would be read aloud).
2. Add pauses with \`${TOOL_NAMES.editAiVoiceover}\`, inserting \`<pause seconds="N"/>\` tokens in
   the replacement text.

Placement taste: after the opening hook, before a key point, ahead of the closing line.
Intentional and sparing — not between every sentence.

Once pauses exist they are transcript tokens like words: any word range you pass
(\`${TOOL_NAMES.addVoiceoverScene}\`, \`${TOOL_NAMES.splitSceneGroup}\`) must include the pause
tokens that fall inside it, written exactly as they appear.

## Pause token mechanics (${TOOL_NAMES.editAiVoiceover})
The transcript is one stream of tokens; a pause is just another token, written
\`<pause seconds="N"/>\` exactly as shown. \`find_phrase\`/\`replacement\` are literal token
find-and-replace: \`find_phrase\` must match one run of the transcript exactly and uniquely —
every word AND every \`<pause/>\` inside the run must appear in it (a \`<pause/>\` in
\`find_phrase\` matches any present pause; its N is ignored on match). The matched run is replaced
token-for-token; everything outside is untouched.
- **Add** a pause: include a \`<pause/>\` tag in the replacement.
- **Remove** one: match it in \`find_phrase\` and omit it from the replacement.
- **Move or resize** one: match it, then reposition it / change its N in the replacement. N is a
  whole number of seconds, 1-9.
- A \`<pause/>\` inside the run that's left out of \`find_phrase\` makes the match fail — include
  every one.

## Rewording, re-voicing, speed
All three go through \`${TOOL_NAMES.editAiVoiceover}\`. A phrase replacement re-synthesizes only the
changed span; a voice or speed change re-synthesizes the whole voiceover. Overlays and scene
timings re-align automatically — but re-check overlay text afterwards, since on-screen text must
stay within what the new words actually say.
To drop words, pass them as \`find_phrase\` with an empty \`replacement\`; a scene left with
no words is removed.
To get a fresh take of a stretch that sounds wrong, pass the same text as both \`find_phrase\` and
\`replacement\`.

## Overlays or clean?
An avatar can be enriched with overlays or left clean. Decide before building, with
\`${TOOL_NAMES.askUserQuestion}\` — you must ASK unless the request is clearly one or the other.
"Make me an avatar video" is NOT clear. Signals: "just the avatar" / "no overlays" / "keep it
clean" mean clean; "add lower thirds", "cut in some b-roll", "make it dynamic" mean overlays. If
the user has no preference, the default is overlays. A clean avatar is essentially done once the
scene group exists — review and finish, and do NOT add overlays.

## Interspersed overlays — do NOT cover every word
Unlike a plain voiceover video, the presenter is meant to be seen. Overlays sit on top of the
avatar at emphasis points, and you cut back to the bare avatar scene between them. Uncovered
stretches are correct and expected — the presenter showing through is the desired look, not a
gap to fill. Don't chase coverage.
- Typical overlays: a lower third (a name/title or a key term), a callout (a point or stat as
  it's said), an occasional b-roll overlay of relevant media.
- Place them where they earn it: introducing a new topic, a stat or key term worth reinforcing,
  something concrete that benefits from a visual.
- **Anchor each overlay that illustrates something on the words that actually mention it**, not
  on the start of the sentence building up to it. Text or media that appears before the avatar
  says the thing reads as dissonant. When such an overlay needs more time on screen, extend it
  forward into the following speech rather than backwards into the run-up. An overlay that
  identifies rather than illustrates — a lower third — has nothing in the speech to anchor to and
  belongs over the opening words.
- Density: interspersed, not continuous. Don't run overlay after overlay without returning to the
  avatar, and don't blanket the timeline with media — fewer, well-placed overlays beat wall-to-
  wall coverage.

## Adjusting or removing an overlay
Re-time an overlay with \`${TOOL_NAMES.setSceneRange}\` (change which transcript words it covers —
grow, shrink, or shift it) or drop it with \`${TOOL_NAMES.deleteScene}\`. Either just exposes the
avatar underneath, which is the intended look here — not a gap to re-cover.

## Designs and text
- Favour lower thirds, callouts, and b-roll designs; match each overlay's design to its job, and
  vary the design from one overlay to the next.
- Overlay text stays short and punchy — a name, a title, a key phrase or stat — never the
  transcript.

${narrationTimingGuidance()}

## B-roll media
Load the \`${SKILL_NAMES.findStockMedia}\` skill before your first b-roll search. Each b-roll
overlay illustrates one specific moment — it should be immediately obvious what it's reinforcing.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- confirm that no overlay, callout, or text may cover the avatar's face
- confirm that the avatar stays well framed on all scenes
NOTE: Uncovered transcript ranges are NOT defects here.

## Publishing
Avatar videos can always be built and previewed. Some teams can't publish them; if so you'll be
told in a note after building, and if the user then asks to publish or download you should call
\`${TOOL_NAMES.suggestUpgrade}\` with feature "ai_avatar" first.`;
    },
};
